import React from 'react';
import { Link } from 'react-router-dom';
import type { SnapshotChip, SnapshotTone } from '../../../utils/dashboard/healthSnapshot';

const TONE: Record<SnapshotTone, string> = {
  neutral: 'var(--melio-ink)',
  attention: 'var(--melio-terracotta-deep)',
  muted: 'var(--melio-text-muted)',
};

/**
 * "Your health at a glance" — a hairline-ruled strip rather than a row of
 * cards. Ten stacked white cards would read as a wall, and this block is a
 * summary of the ones below it, not a peer of them.
 *
 * Chips arrive already filtered: a source that failed to load has no chip at
 * all, so nothing here needs to guard against rendering a misleading zero.
 *
 * Every chip that has an `href` is a link. This strip is where a member most
 * often learns something needs their attention — "3 outside" range, a history
 * that is 60% complete — and until now it was inert text, so the sentence that
 * raised the concern was also the dead end. The link wraps the value rather
 * than the whole cell: `<a>` cannot legally contain the `<dt>`/`<dd>` pair, and
 * the value is the part the member is reacting to.
 */
export default function HealthSnapshotStrip({ chips }: { chips: SnapshotChip[] }) {
  if (chips.length === 0) return null;

  return (
    <section aria-labelledby="snapshot-heading">
      <h2
        id="snapshot-heading"
        className="text-[11px] uppercase tracking-[0.22em] mb-3"
        style={{ color: 'var(--melio-olive-deep)' }}
      >
        Your health
      </h2>
      <dl
        className="grid gap-px"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          backgroundColor: 'var(--melio-border)',
          border: '1px solid var(--melio-border)',
        }}
      >
        {chips.map((chip) => (
          <div key={chip.id} className="px-4 py-3.5" style={{ backgroundColor: 'white' }}>
            <dt
              className="text-[10px] uppercase tracking-[0.16em] mb-1.5"
              style={{ color: 'var(--melio-text-muted)' }}
            >
              {chip.label}
            </dt>
            <dd className="text-sm leading-snug" style={{ color: TONE[chip.tone], fontWeight: 500 }}>
              {chip.href ? (
                <Link
                  to={chip.href}
                  // The accessible name carries the label too: "3 outside" is
                  // not a destination anyone can identify from a link list.
                  aria-label={`${chip.label}: ${chip.value}`}
                  className="underline underline-offset-2 decoration-1"
                  style={{ color: 'inherit', textDecorationColor: 'var(--melio-border)' }}
                >
                  {chip.value}
                </Link>
              ) : (
                chip.value
              )}
            </dd>
            {chip.hint && (
              <p className="text-[11px] mt-1" style={{ color: 'var(--melio-text-muted)' }}>
                {chip.hint}
              </p>
            )}
          </div>
        ))}
      </dl>
    </section>
  );
}
